import React from 'react';
import TruncatedTextWithTooltip from '../../../../components/truncated-text-with-tooltip';
import { Permissions } from '../../../../utils/access-control/enums/permissions';
import hasPermission from '../../../../utils/access-control/has-permission';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';
import Icon from '../../atoms/icon';
import { Overlay, Placement, Tooltip, OverlayTooltip } from '../../overlay';
import ContactStatusTag from '../../atoms/contact-status-tag/contact-status-tag';

const getContactName = (firstName, lastName) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  return lastName;
};

const iconStyle = { marginLeft: '.25rem', marginTop: '.15rem' };

const iconConfigs = {
  skip: {
    identifier: 'circle-check',
    toolTipText: 'Unverified',
    className: 'gray-txt-23',
  },
  valid: {
    identifier: 'circle-check',
    toolTipText: 'Valid',
    className: 'green-txt-14',
  },
  risky: {
    identifier: 'danger',
    toolTipText: 'Risky',
    className: 'orange-txt-6',
  },
  bad: {
    identifier: 'warning',
    toolTipText: 'Bad',
    className: 'red-txt-6',
  },
  unknown: {
    identifier: 'danger',
    toolTipText: 'Risky',
    className: 'red-txt-6',
  },
  inProgress: {
    identifier: 'rotate-clockwise',
    toolTipText: 'In Progress',
    className: 'gray-txt-23',
  },
};

const ContactNameField = ({
  cell,
  onClick = null,
  status = null,
  maxEmailLength = 25,
  verificationStatus = '',
  conditionEvaluation = null,
  showProspectDetailsTooltipOnEmail = false,
}) => {
  const field = {
    Email: '',
    'First Name': '',
    'Last Name': '',
  };
  cell?.forEach((contactField) => {
    if (
      contactField?.label === 'Email' ||
      contactField?.label === 'First Name' ||
      contactField?.label === 'Last Name'
    ) {
      field[contactField.label] = contactField.value;
    }
  });

  const iconConfig = iconConfigs[verificationStatus];

  const getVerificationIcon = () => (
    <Icon
      identifier={iconConfig?.identifier}
      className={iconConfig?.className}
      style={iconStyle}
    />
  );

  const verificationToolTip = (
    <Overlay
      placement={Placement.Right}
      overlay={<Tooltip id={cell?.id} text={iconConfig?.toolTipText} />}
    >
      {getVerificationIcon()}
    </Overlay>
  );

  const contactName = getContactName(field['First Name'], field['Last Name']);

  const contactNameField = contactName ? (
    <span
      className={`mr-2 ${
        showProspectDetailsTooltipOnEmail ? 'contact-name-field-tooltip' : ''
      }`}
      {...(showProspectDetailsTooltipOnEmail
        ? accessibleOnClick(() => !!onClick && onClick(cell[0]?.id))
        : {})}
    >
      {showProspectDetailsTooltipOnEmail ? (
        <div className="contact-name-text-container">
          <OverlayTooltip text={contactName} placement={Placement.Bottom}>
            <span className="contact-name-col">{contactName}</span>
          </OverlayTooltip>
        </div>
      ) : (
        <TruncatedTextWithTooltip
          string={contactName}
          length={20}
          tooltipClassName="contact-field-name-tooltip"
        />
      )}
    </span>
  ) : (
    <span
      className={`semibold-2 gray-txt-11 mr-2 font-italic ${
        showProspectDetailsTooltipOnEmail ? 'no-name' : ''
      }`}
      {...(showProspectDetailsTooltipOnEmail
        ? accessibleOnClick(() => !!onClick && onClick(cell[0]?.id))
        : {})}
    >
      No Name
    </span>
  );

  const emailField = (
    <div
      className={`email-field bs-mt-2 ${
        showProspectDetailsTooltipOnEmail ? 'email-field-tooltip' : ''
      }`}
    >
      <div
        className={`${
          showProspectDetailsTooltipOnEmail
            ? 'email-field-inner-container-tooltip'
            : 'd-flex align-items-center'
        }`}
      >
        <div
          className={`${
            showProspectDetailsTooltipOnEmail
              ? 'email-field-tooltip-container'
              : ''
          }`}
        >
          {showProspectDetailsTooltipOnEmail ? (
            <OverlayTooltip text={field.Email} placement={Placement.Bottom}>
              <div className="contact-email-text-container w-100">
                <span className="contact-email-col">{field.Email}</span>
              </div>
            </OverlayTooltip>
          ) : (
            <TruncatedTextWithTooltip
              string={`${field.Email} `}
              length={maxEmailLength}
              tooltipClassName="contact-field-name-tooltip"
            />
          )}
        </div>
        {hasPermission(Permissions.PROSPECT_UPDATE) &&
          iconConfig &&
          verificationToolTip}
      </div>
    </div>
  );

  const contactContent = (
    <div
      className="contact-field-name-container"
      // * Using cell[0].id as the cell contains dynamic fields value
      // * for gettings row details, can't loop through as it doesn't
      // * have any fixed id or key
      {...(!showProspectDetailsTooltipOnEmail
        ? accessibleOnClick(() => !!onClick && onClick(cell[0]?.id))
        : {})}
    >
      <div className="name-field w-100">
        <div className="d-flex">
          {contactNameField}
          <div>
            <ContactStatusTag
              status={status}
              isSequenceProspectTag={true}
              showTooltip={conditionEvaluation === false}
            />
          </div>
        </div>
      </div>
      {emailField}
    </div>
  );

  return contactContent;
};

export default ContactNameField;
